import React from 'react';
import styled from 'styled-components';
import { analyticsTrackEvent } from '../../core/analytics';
import { DOWNLOAD_DRIVER_CLICK_LINK } from '../../core/analytics/events';
import { getSolvAppStoreLink, getSolvPlayStoreLink } from './util';
import { isIOS } from '../../core/util/device';
import { useLDFlags } from '~/core/launch-darkly/hooks/useLDFlags';

const badgeHeight = `40px`;

const Link = styled.a.withConfig({ componentId: 'sc-1pj30o6-0' })`
  display: inline-block;
  overflow: hidden;
  width: 135px;
  height: ${badgeHeight};
`;

const Img = styled.img.withConfig({ componentId: 'sc-1pj30o6-1' })`
  height: ${badgeHeight};
`;

type Props = {
  /** Campaign name to track */
  campaignName?: string;

  /** Class name to append to the link */
  className?: string;

  /** Which app store badge to show */
  store?: 'ios' | 'android';

  /** Whether to open in a new window */
  newWindow?: boolean;
};

/**
 * App store / Play store badge. Defaults to Play store, but shows App Store by default on iOS devices.
 */
const AppStoreBadge = ({ campaignName, className, store, newWindow }: Props) => {
  const deviceIsIos = store ? store === 'ios' : isIOS();

  const href = deviceIsIos ? getSolvAppStoreLink(campaignName) : getSolvPlayStoreLink(campaignName);
  const badgeUrl = deviceIsIos
    ? '/images/apple-app-store-badge.svg' // Apple Marketing Assets - https://developer.apple.com/app-store/marketing/guidelines/ || https://tools.applemediaservices.com/app/1464601606?country=us
    : '/images/google-play-badge.png';

  const badgeDescription = deviceIsIos ? 'Download on the App Store' : 'Get it on Google Play';

  return (
    <Link
      className={className}
      href={href}
      onClick={() =>
        analyticsTrackEvent(DOWNLOAD_DRIVER_CLICK_LINK, {
          campaignName,
        })
      }
      target={newWindow ? '_BLANK' : undefined}
    >
      <Img alt={badgeDescription} loading="lazy" src={badgeUrl} />
    </Link>
  );
};

export default AppStoreBadge;
